import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { BsThreeDotsVertical } from "react-icons/bs";

// Registro module-level: un solo menú kebab abierto a la vez en toda la app.
// Como el estado de apertura ahora vive dentro de cada instancia (antes era un
// único `menuAcciones` en el padre), al abrir uno cerramos el que estuviera abierto.
let closeActiveMenu = null;

/**
 * <KebabMenu> — botón "⋮" con menú de acciones (patrón 10.136 de la arquitectura).
 *
 * Encapsula: el botón `⋮`, el estado abierto/cerrado, el posicionamiento
 * (`position:fixed` anclado al `getBoundingClientRect()` del botón → escapa el
 * overflow de las tablas), el portal a `document.body`, el cierre por
 * elegir ítem / click-fuera (ignora `[data-kebab]`) / Escape, y "uno abierto a
 * la vez" vía registro module-level.
 *
 * Props:
 *  - items: Array<{ label, onClick, disabled?, tooltip?, danger?, icon? }>.
 *           Solo renderiza los ítems que se le pasan. Si un ítem va `disabled`,
 *           lo muestra gris con su `tooltip`. Si `items` está vacío → no renderiza nada.
 *  - minWidth: ancho mínimo del menú (default 160, como EdificiosTable/Factoring).
 *  - buttonClassName: clase del botón (default estilo canónico `btn btn-sm btn-light`).
 *  - ariaLabel: title/aria-label del botón (default "Acciones").
 */
const KebabMenu = ({
  items = [],
  minWidth = 160,
  buttonClassName = "btn btn-sm btn-light",
  ariaLabel = "Acciones",
}) => {
  const [pos, setPos] = useState(null); // { top, right } | null
  const menuRef = useRef(null);

  const close = useCallback(() => {
    setPos(null);
    if (closeActiveMenu === close) closeActiveMenu = null;
  }, []);

  // Cierre por click-fuera (ignora el propio botón vía [data-kebab]) + Escape.
  useEffect(() => {
    if (!pos) return undefined;
    const onDown = (e) => {
      if (e.target.closest("[data-kebab]")) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pos, close]);

  // Si se desmonta con el menú abierto, liberar el registro global.
  useEffect(
    () => () => {
      if (closeActiveMenu === close) closeActiveMenu = null;
    },
    [close]
  );

  if (!items || items.length === 0) return null;

  const toggle = (e) => {
    if (pos) {
      close();
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    if (closeActiveMenu && closeActiveMenu !== close) closeActiveMenu();
    closeActiveMenu = close;
    setPos({ top: r.bottom + 2, right: window.innerWidth - r.right });
  };

  return (
    <>
      <button
        type="button"
        data-kebab="true"
        className={buttonClassName}
        title={ariaLabel}
        aria-label={ariaLabel}
        onClick={toggle}
      >
        <BsThreeDotsVertical />
      </button>
      {pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: pos.top,
              right: pos.right,
              zIndex: 2000,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              minWidth,
              padding: "4px 0",
            }}
          >
            {items.map((a) => (
              <button
                key={a.label}
                type="button"
                disabled={a.disabled}
                title={a.tooltip}
                onMouseEnter={(e) => {
                  if (!a.disabled) e.currentTarget.style.background = "#f8f9fa";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "none";
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "6px 16px",
                  border: "none",
                  background: "none",
                  fontSize: "0.85rem",
                  cursor: a.disabled ? "not-allowed" : "pointer",
                  color: a.disabled ? "#adb5bd" : a.danger ? "#c62828" : "#212529",
                  whiteSpace: "nowrap",
                }}
                onClick={() => {
                  if (a.disabled) return;
                  close();
                  a.onClick();
                }}
              >
                {a.icon ? <span style={{ marginRight: 8 }}>{a.icon}</span> : null}
                {a.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default KebabMenu;
